// Barra da emissão em sequência da Planilha Gran. Fica no canto inferior esquerdo, por
// cima das janelas do fluxo (z-[80] > validação z-[70] > emissão z-50) e longe do rodapé
// da janela de emissão, onde fica o botão "Emitir".
import React from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Loader2, PauseCircle, Play, SkipForward, X } from 'lucide-react';
import type { FilaGran } from './filaEmissaoGran';

interface Props {
  fila: FilaGran;
  onContinuar: () => void;
  onPular: () => void;
  onEncerrar: () => void;
}

const FilaEmissaoGranBar: React.FC<Props> = ({ fila, onContinuar, onPular, onEncerrar }) => {
  const total = fila.itens.length;
  const terminou = fila.pos >= total;
  const atual = fila.itens[fila.pos];
  const feitos = fila.emitidas.length + fila.puladas.length;

  return createPortal(
    <div
      className="fixed bottom-4 left-4 z-[80] w-[min(360px,calc(100vw-2rem))] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden"
      role="status"
      aria-live="polite"
    >
      <div className="h-1 bg-gray-100 dark:bg-gray-800">
        <div className="h-full bg-primary transition-all" style={{ width: `${total ? (feitos / total) * 100 : 0}%` }} />
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          {terminou
            ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" aria-hidden="true" />
            : fila.pausada
              ? <PauseCircle className="w-5 h-5 text-amber-500 shrink-0" aria-hidden="true" />
              : <Loader2 className="w-5 h-5 text-primary animate-spin shrink-0" aria-hidden="true" />}
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-gray-900 dark:text-white">
              {terminou ? 'Emissão em sequência concluída' : `Emissão Gran · ${fila.pos + 1} de ${total}`}
            </p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              {fila.emitidas.length} emitida{fila.emitidas.length !== 1 ? 's' : ''} · {fila.puladas.length} pulada{fila.puladas.length !== 1 ? 's' : ''}
            </p>
            {!terminou && atual && <p className="text-[12px] text-gray-700 dark:text-gray-200 truncate mt-0.5" title={atual.rotulo}>{atual.rotulo}</p>}
            {!terminou && fila.pausada && (
              <p className="text-[11px] font-medium text-amber-700 dark:text-amber-300 mt-0.5">{fila.motivoPausa || 'Pausada: a janela desta fatura foi fechada sem emitir.'}</p>
            )}
          </div>
          <button type="button" onClick={onEncerrar} className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label={terminou ? 'Fechar' : 'Encerrar a emissão em sequência'} title={terminou ? 'Fechar' : 'Encerrar (as que já saíram continuam emitidas)'}>
            <X className="w-4 h-4" />
          </button>
        </div>
        {!terminou && fila.pausada && (
          <div className="flex gap-2">
            <button type="button" onClick={onContinuar} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-primary hover:bg-primary/90 text-white">
              <Play className="w-3.5 h-3.5" aria-hidden="true" /> Abrir de novo
            </button>
            <button type="button" onClick={onPular} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">
              <SkipForward className="w-3.5 h-3.5" aria-hidden="true" /> Pular esta
            </button>
          </div>
        )}
        {!terminou && !fila.pausada && (
          <button type="button" onClick={onPular} className="text-[11px] font-medium text-gray-500 hover:text-primary">
            Pular esta fatura
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default FilaEmissaoGranBar;
